import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PushPinIcon from '@mui/icons-material/PushPin';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import { supabase } from '../config/supabase';
import type { AvailabilityStatus, LineupStatus, LookupState, MatchSiteType, OpponentRatingBasis, PlayerRatingBasis, UserPreferences } from '../types';
import { loadParticipationReportRows, type ParticipationReportRow } from '../utils/participationReport';
import { lookupLabel } from '../data/lookups';
import {
  defaultStrategyForAdvancementType,
  generateSeasonPlanningAnalysis,
  normalizeMultiTeamSchedulingConfig,
  type AdvancementType,
  type RecommendationPlayer,
  type RecommendationPosition,
  type RecommendationStrategyProfile,
  type SeasonPlanningAnalysis,
  type SeasonPlanningMatch,
  type SeasonRecommendationPlan,
  type UnknownAvailabilityPolicy,
} from '../utils/lineupRecommendationEngine';
import { useAuth } from '../auth/AuthContext';
import { formatPlayerName } from '../utils/playerPreferences';
import { applyTeamRecommendationSettings, type TeamRecommendationSettings } from '../utils/teamRecommendationSettings';
import { isDesignatedSubstitute, normalizeRosterRole, type RosterRole } from '../utils/rosterRoles';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';

type SeasonPlanningProps = {
  lookups: LookupState;
  defaultCaptainTeamIds: string[];
  userPreferences: UserPreferences;
  readOnly?: boolean;
};

type MatchRow = {
  id: string;
  team_id: string;
  season_id: string;
  league_configuration_id: string;
  match_classification: string | null;
  match_date: string;
  match_time: string | null;
  warmup_time: string | null;
  match_status: string;
  lineup_status: LineupStatus;
  match_site_type: MatchSiteType;
  opponent_name: string | null;
  opponent_average_edr: number | null;
  opponent_rating_basis?: OpponentRatingBasis | null;
  opponent_subjective_difficulty?: number | null;
  planning_priority?: 'development' | 'normal' | 'important' | 'must_win';
};

type RosterRow = {
  player_id: string;
  player_tier: string | null;
  roster_status: string | null;
  roster_role: RosterRole | string | null;
  target_match_count: number | null;
};

type PlayerRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  sex: 'male' | 'female';
  estimated_dynamic_rating: number | null;
  utr_singles_rating: number | null;
  utr_doubles_rating: number | null;
  singles_preference?: 'yes' | 'if_possible' | 'no' | null;
};

type AvailabilityRow = {
  match_event_id: string;
  player_id: string;
  availability_status: AvailabilityStatus;
};

type EntryRow = {
  id: string;
  match_event_id: string;
  league_configuration_position_id: string;
  position_code: string;
  slot_number: number;
  player_id: string;
};

type PositionRow = {
  id: string;
  position_code: string;
  display_order: number;
  line_type: string;
  required_player_count: number;
  required_male_count: number;
  required_female_count: number;
};

type TeamStrategyRow = TeamRecommendationSettings & {
  season_id: string;
  league_configuration_id: string;
  workspace_id: string | null;
  advancement_type: AdvancementType | null;
  recommendation_strategy_profile_id: string | null;
  player_rating_basis?: PlayerRatingBasis | null;
};

type StrategyProfileRow = {
  id: string;
  name: string;
  advancement_type: AdvancementType;
  version: number;
  competitiveness_weight: number;
  participation_fairness_weight: number;
  singles_opportunity_weight: number;
  schedule_spacing_weight: number;
  partner_compatibility_weight: number;
  home_away_balance_weight: number;
  singles_edr_weight: number;
  singles_utr_weight: number;
  doubles_edr_weight: number;
  doubles_utr_weight: number;
  change_sensitivity: RecommendationStrategyProfile['changeSensitivity'];
  tentative_player_policy: RecommendationStrategyProfile['tentativePlayerPolicy'];
  preferred_pair_bonus?: number;
  preferred_pair_separation_penalty?: number;
  strongly_preferred_pair_bonus?: number;
  strongly_preferred_pair_separation_penalty?: number;
  avoid_pairing_penalty?: number;
  poor_compatibility_penalty?: number;
  good_compatibility_bonus?: number;
  excellent_compatibility_bonus?: number;
  multi_team_scheduling_config?: unknown;
  enforce_target_matches?: boolean | null;
  target_match_variance?: number | null;
  enforce_max_sit?: boolean | null;
  max_weeks_without_match?: number | null;
  use_partner_compatibility?: boolean | null;
  use_partner_scheduling_preferences?: boolean | null;
};

type PlanningContext = {
  teamLabel: string;
  seasonId: string;
  workspaceId: string;
  generatedAt: string;
  matches: SeasonPlanningMatch[];
  positions: RecommendationPosition[];
  players: RecommendationPlayer[];
  strategy: RecommendationStrategyProfile;
  includePublished: boolean;
};

type ProposalSlot = {
  matchId: string;
  leagueConfigurationPositionId: string;
  positionCode: string;
  lineType: string;
  slotNumber: number;
  currentPlayerId: string | null;
  generatedPlayerId: string | null;
  proposedPlayerId: string | null;
  isPinned: boolean;
  isExcludedFromApply: boolean;
  editSource: 'engine' | 'captain' | 'restored';
  editedByName: string;
};

type ProposalWorkspace = {
  planId: string;
  planName: string;
  planType: string;
  status: 'generated' | 'editing' | 'ready_to_apply' | 'applied' | 'discarded';
  generatedAt: string;
  lastEditedAt: string | null;
  slots: ProposalSlot[];
  excludedMatchIds: string[];
};

type ProposalValidation = {
  blockingErrors: string[];
  warnings: string[];
  changedAssignments: number;
  eligibleMatches: number;
  protectedMatches: number;
  added: number;
  replaced: number;
  removed: number;
  publishedAffected: number;
};
type SeasonPlanningViewState = {
  teamId: string;
  teamScopeMode?: 'default' | 'explicit';
  mode: 'season_baseline' | 'remaining_season';
  unknownPolicy: UnknownAvailabilityPolicy;
  includeTentative: boolean;
  preserveDraft: boolean;
  includePublished: boolean;
};

function slotKey(slot: Pick<ProposalSlot, 'matchId' | 'leagueConfigurationPositionId' | 'slotNumber'>): string {
  return `${slot.matchId}:${slot.leagueConfigurationPositionId}:${slot.slotNumber}`;
}

function strategyFromRow(row: StrategyProfileRow): RecommendationStrategyProfile {
  return {
    id: row.id,
    name: row.name,
    advancementType: row.advancement_type,
    version: row.version,
    competitivenessWeight: row.competitiveness_weight,
    participationFairnessWeight: row.participation_fairness_weight,
    singlesOpportunityWeight: row.singles_opportunity_weight,
    scheduleSpacingWeight: row.schedule_spacing_weight,
    partnerCompatibilityWeight: row.partner_compatibility_weight,
    homeAwayBalanceWeight: row.home_away_balance_weight,
    singlesEdrWeight: row.singles_edr_weight,
    singlesUtrWeight: row.singles_utr_weight,
    doublesEdrWeight: row.doubles_edr_weight,
    doublesUtrWeight: row.doubles_utr_weight,
    changeSensitivity: row.change_sensitivity,
    tentativePlayerPolicy: row.tentative_player_policy,
    preferredPairBonus: row.preferred_pair_bonus ?? 20,
    preferredPairSeparationPenalty: row.preferred_pair_separation_penalty ?? -10,
    stronglyPreferredPairBonus: row.strongly_preferred_pair_bonus ?? 40,
    stronglyPreferredPairSeparationPenalty: row.strongly_preferred_pair_separation_penalty ?? -30,
    avoidPairingPenalty: row.avoid_pairing_penalty ?? -20,
    poorCompatibilityPenalty: row.poor_compatibility_penalty ?? -10,
    goodCompatibilityBonus: row.good_compatibility_bonus ?? 15,
    excellentCompatibilityBonus: row.excellent_compatibility_bonus ?? 30,
    multiTeamScheduling: normalizeMultiTeamSchedulingConfig(row.multi_team_scheduling_config),
    enforceTargetMatches: row.enforce_target_matches === true,
    targetMatchVariance: (row.target_match_variance === 0 || row.target_match_variance === 2 ? row.target_match_variance : 1),
    enforceMaxSit: row.enforce_max_sit === true,
    maxWeeksWithoutMatch: row.max_weeks_without_match ?? 2,
    usePartnerCompatibility: row.use_partner_compatibility !== false,
    usePartnerSchedulingPreferences: row.use_partner_scheduling_preferences !== false,
  };
}

function confidenceColor(confidence: string): 'success' | 'warning' | 'error' {
  if (confidence === 'High') return 'success';
  if (confidence === 'Moderate') return 'warning';
  return 'error';
}

function effectiveLineupStatus(lineupStatus: LineupStatus, activeEntryCount: number): LineupStatus {
  if (activeEntryCount === 0 && lineupStatus === 'Published') return 'Not Started';
  return lineupStatus;
}

function isProtectedMatch(match: SeasonPlanningMatch, includePublished: boolean): boolean {
  return match.matchStatus === 'Completed'
    || match.matchStatus === 'Cancelled'
    || (match.lineupStatus === 'Published' && !includePublished);
}

function protectedMatchReason(match: SeasonPlanningMatch, includePublished: boolean): string | null {
  if (match.matchStatus === 'Completed') return 'Completed - historical lineup cannot be changed';
  if (match.matchStatus === 'Cancelled') return 'Cancelled - excluded from application';
  if (match.lineupStatus === 'Published' && !includePublished) return 'Published - enable Published edits to change';
  return null;
}

function playerLabel(player: RecommendationPlayer | undefined, userPreferences: UserPreferences): string {
  if (!player) return 'Empty';
  return formatPlayerName({ first_name: player.firstName, last_name: player.lastName, sex: player.sex }, userPreferences) || player.id;
}

function playerDetail(player: RecommendationPlayer | undefined, assignmentType: 'singles' | 'doubles', availability: AvailabilityStatus | undefined): string {
  if (!player) return 'No player assigned';
  const rating = player.estimatedDynamicRating === null ? 'EDR -' : `EDR ${player.estimatedDynamicRating.toFixed(2)}`;
  const utr = assignmentType === 'singles'
    ? player.utrSinglesRating === null ? 'Singles UTR -' : `Singles UTR ${player.utrSinglesRating}`
    : player.utrDoublesRating === null ? 'Doubles UTR -' : `Doubles UTR ${player.utrDoublesRating}`;
  return `${availability || 'unknown'} • ${rating} • ${utr} • projected ${player.completedPlayed + player.futureSingles + player.futureDoubles}/${player.targetMatches}`;
}

function buildProposalWorkspace(plan: SeasonRecommendationPlan, context: PlanningContext): ProposalWorkspace {
  const changesBySlot = new Map(plan.changes.map((change) => [`${change.matchEventId}:${change.leagueConfigurationPositionId}:${change.slotNumber}`, change]));
  const slots: ProposalSlot[] = [];
  context.matches.forEach((match) => {
    const entriesBySlot = new Map(match.entries.map((entry) => [`${entry.leagueConfigurationPositionId}:${entry.slotNumber}`, entry]));
    context.positions.forEach((position) => {
      for (let slotNumber = 1; slotNumber <= position.requiredPlayerCount; slotNumber += 1) {
        const currentEntry = entriesBySlot.get(`${position.id}:${slotNumber}`);
        const change = changesBySlot.get(`${match.id}:${position.id}:${slotNumber}`);
        const generatedPlayerId = change ? change.newPlayerId : currentEntry?.playerId || null;
        slots.push({
          matchId: match.id,
          leagueConfigurationPositionId: position.id,
          positionCode: position.positionCode,
          lineType: position.lineType,
          slotNumber,
          currentPlayerId: currentEntry?.playerId || null,
          generatedPlayerId,
          proposedPlayerId: generatedPlayerId,
          isPinned: false,
          isExcludedFromApply: false,
          editSource: 'engine',
          editedByName: 'CourtCaptain',
        });
      }
    });
  });
  return {
    planId: plan.id,
    planName: plan.name,
    planType: plan.planType,
    status: 'generated',
    generatedAt: context.generatedAt,
    lastEditedAt: null,
    slots,
    excludedMatchIds: context.matches.filter((match) => isProtectedMatch(match, context.includePublished)).map((match) => match.id),
  };
}

function validateWorkspace(workspace: ProposalWorkspace | null, context: PlanningContext | null): ProposalValidation {
  if (!workspace || !context) {
    return { blockingErrors: [], warnings: [], changedAssignments: 0, eligibleMatches: 0, protectedMatches: 0, added: 0, replaced: 0, removed: 0, publishedAffected: 0 };
  }
  const playersById = new Map(context.players.map((player) => [player.id, player]));
  const matchesById = new Map(context.matches.map((match) => [match.id, match]));
  const positionsById = new Map(context.positions.map((position) => [position.id, position]));
  const blockingErrors: string[] = [];
  const warnings: string[] = [];
  let added = 0;
  let replaced = 0;
  let removed = 0;
  let publishedAffected = 0;

  context.matches.forEach((match) => {
    const matchSlots = workspace.slots.filter((slot) => slot.matchId === match.id);
    const protectedReason = protectedMatchReason(match, context.includePublished);
    const changedProtectedSlots = matchSlots.filter((slot) => slot.proposedPlayerId !== slot.currentPlayerId && !slot.isExcludedFromApply);
    if (protectedReason && changedProtectedSlots.length > 0) blockingErrors.push(`${match.opponentName}: ${protectedReason}.`);
    const proposedPlayerIds = matchSlots.map((slot) => slot.proposedPlayerId).filter((playerId): playerId is string => Boolean(playerId));
    const duplicatePlayerIds = proposedPlayerIds.filter((playerId, index) => proposedPlayerIds.indexOf(playerId) !== index);
    [...new Set(duplicatePlayerIds)].forEach((playerId) => blockingErrors.push(`${playerLabel(playersById.get(playerId), { playerNameDisplayFormat: 'first_last', playerSortOrder: 'last_name', groupPlayersBySex: false })} is assigned twice vs ${match.opponentName}.`));
    context.positions.forEach((position) => {
      const lineSlots = matchSlots.filter((slot) => slot.leagueConfigurationPositionId === position.id);
      const assignedPlayers = lineSlots.map((slot) => slot.proposedPlayerId ? playersById.get(slot.proposedPlayerId) : null);
      const assignedCount = assignedPlayers.filter(Boolean).length;
      if (!protectedReason && !workspace.excludedMatchIds.includes(match.id) && assignedCount < position.requiredPlayerCount) {
        blockingErrors.push(`${match.opponentName} ${position.positionCode} is not fully assigned.`);
      }
      if (position.requiredMaleCount > 0 && assignedPlayers.filter((player) => player?.sex === 'male').length !== position.requiredMaleCount) {
        blockingErrors.push(`${match.opponentName} ${position.positionCode} does not meet the required male/female composition.`);
      }
      if (position.requiredFemaleCount > 0 && assignedPlayers.filter((player) => player?.sex === 'female').length !== position.requiredFemaleCount) {
        blockingErrors.push(`${match.opponentName} ${position.positionCode} does not meet the required male/female composition.`);
      }
    });
  });

  workspace.slots.forEach((slot) => {
    const match = matchesById.get(slot.matchId);
    const position = positionsById.get(slot.leagueConfigurationPositionId);
    if (!match || !position || slot.isExcludedFromApply || workspace.excludedMatchIds.includes(slot.matchId)) return;
    if (slot.currentPlayerId !== slot.proposedPlayerId) {
      if (!slot.currentPlayerId && slot.proposedPlayerId) added += 1;
      else if (slot.currentPlayerId && !slot.proposedPlayerId) removed += 1;
      else replaced += 1;
      if (match.lineupStatus === 'Published') publishedAffected += 1;
    }
    if (!slot.proposedPlayerId) return;
    const player = playersById.get(slot.proposedPlayerId);
    const availability = match.availabilityByPlayerId[slot.proposedPlayerId] || 'unknown';
    if (!player) blockingErrors.push(`A proposed assignment references a player who is no longer on the active roster.`);
    if (availability === 'not_available') blockingErrors.push(`${playerLabel(player, { playerNameDisplayFormat: 'first_last', playerSortOrder: 'last_name', groupPlayersBySex: false })} is unavailable vs ${match.opponentName}.`);
    if (availability === 'maybe') warnings.push(`${playerLabel(player, { playerNameDisplayFormat: 'first_last', playerSortOrder: 'last_name', groupPlayersBySex: false })} is tentative vs ${match.opponentName}.`);
    if (availability === 'unknown') warnings.push(`${playerLabel(player, { playerNameDisplayFormat: 'first_last', playerSortOrder: 'last_name', groupPlayersBySex: false })} has unknown availability vs ${match.opponentName}.`);
    if (position.lineType === 'singles' && player && player.utrSinglesRating === null && player.estimatedDynamicRating === null) warnings.push(`${playerLabel(player, { playerNameDisplayFormat: 'first_last', playerSortOrder: 'last_name', groupPlayersBySex: false })} is missing singles rating data.`);
    if (position.lineType !== 'singles' && player && player.utrDoublesRating === null && player.estimatedDynamicRating === null) warnings.push(`${playerLabel(player, { playerNameDisplayFormat: 'first_last', playerSortOrder: 'last_name', groupPlayersBySex: false })} is missing doubles rating data.`);
  });

  const protectedMatches = context.matches.filter((match) => isProtectedMatch(match, context.includePublished)).length;
  const excludedEditableMatches = workspace.excludedMatchIds.filter((matchId) => {
    const match = matchesById.get(matchId);
    return match ? !isProtectedMatch(match, context.includePublished) : false;
  }).length;
  const eligibleMatches = context.matches.length - protectedMatches - excludedEditableMatches;
  const changedAssignments = added + replaced + removed;
  return {
    blockingErrors: [...new Set(blockingErrors)],
    warnings: [...new Set(warnings)],
    changedAssignments,
    eligibleMatches: Math.max(0, eligibleMatches),
    protectedMatches,
    added,
    replaced,
    removed,
    publishedAffected,
  };
}

export function SeasonPlanning({ lookups, defaultCaptainTeamIds, userPreferences, readOnly = false }: SeasonPlanningProps) {
  const { appUser, appUserId } = useAuth();
  const defaultViewState = useMemo<SeasonPlanningViewState>(() => ({
    teamId: defaultCaptainTeamIds[0] || '',
    teamScopeMode: 'default',
    mode: 'season_baseline',
    unknownPolicy: 'exclude',
    includeTentative: true,
    preserveDraft: true,
    includePublished: false,
  }), [defaultCaptainTeamIds]);
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: appUserId,
    routeKey: 'season-planning',
    defaultState: defaultViewState,
  });
  const [teamId, setTeamId] = useState(savedViewState.teamId || '');
  const [teamScopeMode, setTeamScopeMode] = useState<'default' | 'explicit'>(savedViewState.teamScopeMode || 'explicit');
  const [mode, setMode] = useState<'season_baseline' | 'remaining_season'>(savedViewState.mode);
  const [unknownPolicy, setUnknownPolicy] = useState<UnknownAvailabilityPolicy>(savedViewState.unknownPolicy);
  const [includeTentative, setIncludeTentative] = useState(savedViewState.includeTentative);
  const [preserveDraft, setPreserveDraft] = useState(savedViewState.preserveDraft);
  const [includePublished, setIncludePublished] = useState(savedViewState.includePublished);
  const [analysis, setAnalysis] = useState<SeasonPlanningAnalysis | null>(null);
  const [planningContext, setPlanningContext] = useState<PlanningContext | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<ProposalWorkspace | null>(null);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  const selectedPlan = useMemo(() => analysis?.plans.find((plan) => plan.id === selectedPlanId) || analysis?.plans[0] || null, [analysis, selectedPlanId]);
  const selectedTeam = lookups.teams.find((team) => team.value === teamId);
  const validation = useMemo(() => validateWorkspace(workspace, planningContext), [workspace, planningContext]);
  const playersById = useMemo(() => new Map((planningContext?.players || []).map((player) => [player.id, player])), [planningContext]);
  const matchesById = useMemo(() => new Map((planningContext?.matches || []).map((match) => [match.id, match])), [planningContext]);
  const positionsById = useMemo(() => new Map((planningContext?.positions || []).map((position) => [position.id, position])), [planningContext]);
  const currentEditorName = appUser?.display_name || 'Current user';

  usePersistentScrollRestoration({ userId: appUserId, routeKey: 'season-planning', ready: !loading });

  useEffect(() => {
    if (!appUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setTeamId(savedViewState.teamScopeMode === 'default' ? defaultCaptainTeamIds[0] || '' : savedViewState.teamId || '');
    setTeamScopeMode(savedViewState.teamScopeMode || 'explicit');
    setMode(savedViewState.mode);
    setUnknownPolicy(savedViewState.unknownPolicy);
    setIncludeTentative(savedViewState.includeTentative);
    setPreserveDraft(savedViewState.preserveDraft);
    setIncludePublished(savedViewState.includePublished);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [appUserId, defaultCaptainTeamIds, loadedStateKey, lookups.teams, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ teamId, teamScopeMode, mode, unknownPolicy, includeTentative, preserveDraft, includePublished });
  }, [includePublished, includeTentative, mode, preserveDraft, setSavedViewState, teamId, teamScopeMode, unknownPolicy, viewStateKey, viewStateReady]);

  useEffect(() => {
    if (teamScopeMode !== 'default' || lookups.teams.length === 0) return;
    const captainId = defaultCaptainTeamIds.find((id) => lookups.teams.some((team) => team.value === id)) || '';
    setTeamId((current) => current === captainId ? current : captainId);
  }, [defaultCaptainTeamIds, lookups.teams, teamScopeMode]);

  useEffect(() => {
    if (lookups.teams.length === 0) return;
    setTeamId((current) => (current && lookups.teams.some((team) => team.value === current) ? current : defaultCaptainTeamIds.find((id) => lookups.teams.some((team) => team.value === id)) || ''));
  }, [defaultCaptainTeamIds, lookups.teams]);

  const buildAnalysis = useCallback(async () => {
    if (!teamId) {
      setMessage({ severity: 'error', text: 'Select a team first.' });
      return;
    }
    setLoading(true);
    setMessage(null);
    setAnalysis(null);
    setWorkspace(null);
    setPlanningContext(null);
    try {
      const teamResult = await supabase
        .from('teams')
        .select('season_id, league_configuration_id, workspace_id, advancement_type, recommendation_strategy_profile_id, player_rating_basis, enforce_target_matches, target_match_variance, enforce_max_sit, max_weeks_without_match, use_partner_compatibility, use_partner_scheduling_preferences')
        .eq('id', teamId)
        .single();
      if (teamResult.error || !teamResult.data) throw new Error(teamResult.error?.message || 'Team not found.');
      const team = teamResult.data as unknown as TeamStrategyRow;

      const [matchesResult, positionsResult, rosterResult, strategyResult, participationResult] = await Promise.all([
        supabase
          .from('match_events')
          .select('id, team_id, season_id, league_configuration_id, match_classification, match_date, match_time, warmup_time, match_status, lineup_status, match_site_type, opponent_name, opponent_average_edr, opponent_rating_basis, opponent_subjective_difficulty, planning_priority')
          .eq('team_id', teamId)
          .eq('season_id', team.season_id)
          .is('deleted_at', null)
          .order('match_date', { ascending: true }),
        supabase
          .from('league_configuration_positions')
          .select('id, position_code, display_order, line_type, required_player_count, required_male_count, required_female_count')
          .eq('league_configuration_id', team.league_configuration_id)
          .eq('is_active', true)
          .is('deleted_at', null)
          .order('display_order', { ascending: true }),
        supabase
          .from('team_rosters')
          .select('player_id, player_tier, roster_status, roster_role, target_match_count')
          .eq('team_id', teamId)
          .eq('is_active', true)
          .in('roster_status', ['active', 'substitute'])
          .is('deleted_at', null),
        team.recommendation_strategy_profile_id
          ? supabase.from('recommendation_strategy_profiles').select('*').eq('id', team.recommendation_strategy_profile_id).maybeSingle()
          : Promise.resolve({ data: null, error: null }),
        loadParticipationReportRows({
          teamId,
          seasonId: team.season_id,
          includeInactivePlayers: false,
          includeSubstitutePlayers: true,
          userPreferences,
          playerLabel: (playerId) => lookupLabel(lookups, 'players', playerId),
        })
          .then((result) => ({ data: result.rows, error: null }))
          .catch((error: unknown) => ({ data: [] as ParticipationReportRow[], error: error instanceof Error ? error : new Error('Unable to load participation report.') })),
      ]);
      if (matchesResult.error || positionsResult.error || rosterResult.error || strategyResult.error || participationResult.error) {
        throw new Error(matchesResult.error?.message || positionsResult.error?.message || rosterResult.error?.message || strategyResult.error?.message || participationResult.error?.message || 'Unable to load season planning inputs.');
      }

      const rosterRows = ((rosterResult.data || []) as unknown as RosterRow[]).map((row) => ({
        ...row,
        roster_role: normalizeRosterRole(row.roster_role),
      }));
      const planningRosterRows = rosterRows.filter((row) => !isDesignatedSubstitute(row));
      const playerIds = planningRosterRows.map((row) => row.player_id);
      const matchRows = (matchesResult.data || []) as unknown as MatchRow[];
      const matchIds = matchRows.map((match) => match.id);
      const [playersResult, availabilityResult, entriesResult, partnerResult] = await Promise.all([
        playerIds.length > 0
          ? supabase
              .from('players')
              .select('id, first_name, last_name, sex, estimated_dynamic_rating, utr_singles_rating, utr_doubles_rating, singles_preference')
              .in('id', playerIds)
              .eq('is_active', true)
              .is('deleted_at', null)
          : Promise.resolve({ data: [], error: null }),
        matchIds.length > 0
          ? supabase.from('availability_records').select('match_event_id, player_id, availability_status').in('match_event_id', matchIds).in('player_id', playerIds).is('deleted_at', null)
          : Promise.resolve({ data: [], error: null }),
        matchIds.length > 0
          ? supabase.from('lineup_entries').select('id, match_event_id, league_configuration_position_id, position_code, slot_number, player_id').in('match_event_id', matchIds).is('deleted_at', null)
          : Promise.resolve({ data: [], error: null }),
        team.workspace_id
          ? supabase.from('partner_preferences').select('workspace_id, player1_id, player2_id, compatibility, pairing_rule, notes').eq('workspace_id', team.workspace_id).eq('is_active', true).is('deleted_at', null)
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (playersResult.error || availabilityResult.error || entriesResult.error || partnerResult.error) {
        throw new Error(playersResult.error?.message || availabilityResult.error?.message || entriesResult.error?.message || partnerResult.error?.message || 'Unable to load season roster details.');
      }

      const participationByPlayer = new Map((participationResult.data || []).map((row) => [row.playerId, row]));
      const rosterByPlayer = new Map(planningRosterRows.map((row) => [row.player_id, row]));
      const recommendationPlayers: RecommendationPlayer[] = ((playersResult.data || []) as unknown as PlayerRow[]).map((player) => {
        const participation = participationByPlayer.get(player.id);
        const roster = rosterByPlayer.get(player.id);
        return {
          id: player.id,
          firstName: player.first_name || '',
          lastName: player.last_name || '',
          sex: player.sex,
          availabilityStatus: 'unknown',
          rosterStatus: roster?.roster_status || 'active',
          rosterRole: roster?.roster_role || 'regular',
          estimatedDynamicRating: player.estimated_dynamic_rating,
          utrSinglesRating: player.utr_singles_rating,
          utrDoublesRating: player.utr_doubles_rating,
          playerTier: roster?.player_tier || null,
          targetMatches: participation?.adjustedTarget ?? roster?.target_match_count ?? 0,
          completedPlayed: participation?.totalPlayed ?? 0,
          futureSingles: 0,
          futureDoubles: 0,
          singlesPlayed: participation?.singlesPlayed ?? 0,
          homeMatches: participation?.homeMatches ?? 0,
          awayMatches: participation?.awayMatches ?? 0,
          neutralMatches: participation?.neutralMatches ?? 0,
          totalAssignmentsForBalance: participation?.totalCompletedAndFutureScheduledMatches ?? 0,
          lastPlayedDate: participation?.lastPlayedDate ?? null,
          lastPlayedWeeksAgo: participation?.lastPlayedWeeksAgo ?? null,
        };
      });
      const availabilityRows = (availabilityResult.data || []) as unknown as AvailabilityRow[];
      const entries = (entriesResult.data || []) as unknown as EntryRow[];
      const seasonMatches: SeasonPlanningMatch[] = matchRows.map((match) => {
        const matchEntries = entries.filter((entry) => entry.match_event_id === match.id);
        return {
          id: match.id,
          teamId: match.team_id,
          seasonId: match.season_id,
          matchDate: match.match_date,
          arrivalDateTime: `${match.match_date}T${match.warmup_time || match.match_time || '00:00:00'}`,
          matchStatus: match.match_status,
          lineupStatus: effectiveLineupStatus(match.lineup_status, matchEntries.length),
          matchSiteType: match.match_site_type,
          opponentName: match.opponent_name || 'Opponent',
          opponentAverageEdr: match.opponent_average_edr,
          opponentRatingBasis: match.opponent_rating_basis || 'avg_edr',
          opponentSubjectiveDifficulty: match.opponent_subjective_difficulty ?? null,
          planningPriority: match.planning_priority || 'normal',
          matchPhase: match.match_classification,
          entries: matchEntries.map((entry) => ({
            id: entry.id,
            matchEventId: entry.match_event_id,
            leagueConfigurationPositionId: entry.league_configuration_position_id,
            positionCode: entry.position_code,
            slotNumber: entry.slot_number,
            playerId: entry.player_id,
          })),
          availabilityByPlayerId: Object.fromEntries(availabilityRows.filter((row) => row.match_event_id === match.id).map((row) => [row.player_id, row.availability_status])),
        };
      });
      const positions: RecommendationPosition[] = ((positionsResult.data || []) as unknown as PositionRow[]).map((position) => ({
        id: position.id,
        positionCode: position.position_code,
        displayOrder: position.display_order,
        lineType: position.line_type,
        requiredPlayerCount: position.required_player_count,
        requiredMaleCount: position.required_male_count,
        requiredFemaleCount: position.required_female_count,
      }));
      const legacyOrDefaultStrategy = strategyResult.data
        ? strategyFromRow(strategyResult.data as unknown as StrategyProfileRow)
        : defaultStrategyForAdvancementType(team.advancement_type || 'non_advancing');
      const strategy = applyTeamRecommendationSettings(legacyOrDefaultStrategy, team);
      const nextAnalysis = generateSeasonPlanningAnalysis({
        mode,
        teamId,
        seasonId: team.season_id,
        matches: seasonMatches,
        positions,
        players: recommendationPlayers,
        partnerPreferences: (partnerResult.data || []) as Parameters<typeof generateSeasonPlanningAnalysis>[0]['partnerPreferences'],
        workspaceId: team.workspace_id || selectedTeam?.workspace_id || '',
        strategy,
        playerRatingBasis: team.player_rating_basis || 'edr',
        singlesPreferenceByPlayerId: Object.fromEntries(((playersResult.data || []) as unknown as PlayerRow[]).map((player) => [player.id, player.singles_preference || 'if_possible'])),
        includePublished,
        preservePublishedAssignments: !includePublished,
        preserveExistingDraftAssignments: preserveDraft,
        preservePinnedAssignments: true,
        unknownAvailabilityPolicy: unknownPolicy,
        includeTentativePlayers: includeTentative,
      });
      setAnalysis(nextAnalysis);
      setPlanningContext({
        teamLabel: selectedTeam?.label || 'Selected team',
        seasonId: team.season_id,
        workspaceId: team.workspace_id || selectedTeam?.workspace_id || '',
        generatedAt: nextAnalysis.generatedAt,
        matches: seasonMatches,
        positions,
        players: recommendationPlayers,
        strategy,
        includePublished,
      });
      setSelectedPlanId(nextAnalysis.plans[0]?.id || null);
    } catch (error) {
      setMessage({ severity: 'error', text: error instanceof Error ? error.message : 'Unable to generate season plan.' });
    } finally {
      setLoading(false);
    }
  }, [includePublished, includeTentative, lookups, mode, preserveDraft, selectedTeam?.workspace_id, teamId, unknownPolicy, userPreferences]);

  const openWorkspace = (plan: SeasonRecommendationPlan) => {
    if (!planningContext) return;
    setWorkspace(buildProposalWorkspace(plan, planningContext));
    setMessage({ severity: 'info', text: 'Proposed lineups are ready for review. No live lineups have been changed.' });
  };

  useEffect(() => {
    if (!workspace || !selectedPlan || workspace.planId === selectedPlan.id || !planningContext) return;
    setWorkspace(null);
  }, [planningContext, selectedPlan, workspace]);

  const updateSlot = (slot: ProposalSlot, proposedPlayerId: string | null, editSource: ProposalSlot['editSource'] = 'captain') => {
    setWorkspace((current) => current
      ? {
          ...current,
          status: 'editing',
          lastEditedAt: new Date().toISOString(),
          slots: current.slots.map((item) => slotKey(item) === slotKey(slot)
            ? { ...item, proposedPlayerId, editSource, editedByName: editSource === 'captain' ? currentEditorName : 'CourtCaptain' }
            : item),
        }
      : current);
  };

  const togglePin = (slot: ProposalSlot) => {
    setWorkspace((current) => current
      ? {
          ...current,
          status: 'editing',
          lastEditedAt: new Date().toISOString(),
          slots: current.slots.map((item) => slotKey(item) === slotKey(slot) ? { ...item, isPinned: !item.isPinned } : item),
        }
      : current);
  };

  const toggleMatchExcluded = (matchId: string) => {
    setWorkspace((current) => {
      if (!current) return current;
      const excluded = new Set(current.excludedMatchIds);
      if (excluded.has(matchId)) excluded.delete(matchId);
      else excluded.add(matchId);
      return { ...current, status: 'editing', lastEditedAt: new Date().toISOString(), excludedMatchIds: [...excluded] };
    });
  };

  const resetWorkspace = () => {
    if (!selectedPlan || !planningContext) return;
    setWorkspace(buildProposalWorkspace(selectedPlan, planningContext));
    setMessage({ severity: 'info', text: 'Proposal reset to the generated recommendation. Live lineups were not changed.' });
  };

  const discardWorkspace = () => {
    setWorkspace(null);
    setMessage({ severity: 'info', text: 'Proposed plan discarded. Live lineups were not changed.' });
  };

  const saveWorkspace = async () => {
    if (!workspace || !analysis || !planningContext || !appUserId) return;
    setLoading(true);
    try {
      const { error } = await supabase.from('season_plans').insert({
        team_id: analysis.teamId,
        season_id: analysis.seasonId,
        plan_type: workspace.planType,
        status: 'generated',
        proposal_status: workspace.status === 'generated' ? 'editing' : workspace.status,
        strategy_profile_id: analysis.strategy.id || null,
        strategy_snapshot: analysis.strategy,
        plan_snapshot: { workspace, validation, context: { teamLabel: planningContext.teamLabel, generatedAt: planningContext.generatedAt } },
        generated_by: appUserId,
        last_edited_by: appUserId,
        editing_started_at: workspace.generatedAt,
        last_edited_at: new Date().toISOString(),
      });
      if (error) throw new Error(error.message);
      setWorkspace({ ...workspace, status: 'editing', lastEditedAt: new Date().toISOString() });
      setMessage({ severity: 'success', text: 'Proposed edits saved. Live lineups were not changed.' });
    } catch (error) {
      setMessage({ severity: 'error', text: error instanceof Error ? error.message : 'Unable to save proposed edits.' });
    } finally {
      setLoading(false);
    }
  };

  const applyWorkspace = async () => {
    if (readOnly || !analysis || !appUserId || !workspace || !planningContext || validation.blockingErrors.length > 0) return;
    setLoading(true);
    setApplyDialogOpen(false);
    setMessage(null);
    try {
      const changes = workspace.slots
        .filter((slot) => !workspace.excludedMatchIds.includes(slot.matchId) && !slot.isExcludedFromApply && slot.currentPlayerId !== slot.proposedPlayerId)
        .map((slot) => ({
          matchEventId: slot.matchId,
          leagueConfigurationPositionId: slot.leagueConfigurationPositionId,
          positionCode: slot.positionCode,
          slotNumber: slot.slotNumber,
          playerId: slot.proposedPlayerId,
          currentPlayerId: slot.currentPlayerId,
          editSource: slot.editSource,
        }));
      const { error: applyError } = await supabase.rpc('apply_season_plan_proposal', {
        p_changes: changes,
        p_allow_published: planningContext.includePublished,
        p_applied_by: appUserId,
      });
      if (applyError) throw new Error(applyError.message);
      await supabase
        .from('season_plans')
        .update({ status: 'superseded', superseded_at: new Date().toISOString() })
        .eq('team_id', analysis.teamId)
        .eq('season_id', analysis.seasonId)
        .eq('status', 'accepted')
        .is('deleted_at', null);
      const { data: seasonPlanRecord, error: planError } = await supabase
        .from('season_plans')
        .insert({
          team_id: analysis.teamId,
          season_id: analysis.seasonId,
          plan_type: workspace.planType,
          status: 'accepted',
          proposal_status: 'applied',
          strategy_profile_id: analysis.strategy.id || null,
          strategy_snapshot: analysis.strategy,
          plan_snapshot: { workspace, validation },
          generated_by: appUserId,
          last_edited_by: appUserId,
          last_edited_at: new Date().toISOString(),
          accepted_by: appUserId,
          accepted_at: new Date().toISOString(),
        })
        .select('id')
        .single();
      if (planError) throw new Error(planError.message);
      const seasonPlanId = (seasonPlanRecord as { id: string }).id;
      const assignmentRows = workspace.slots
        .filter((slot) => slot.proposedPlayerId)
        .map((slot) => ({
          season_plan_id: seasonPlanId,
          match_event_id: slot.matchId,
          league_configuration_position_id: slot.leagueConfigurationPositionId,
          position_code: slot.positionCode,
          slot_number: slot.slotNumber,
          player_id: slot.proposedPlayerId,
          generated_player_id: slot.generatedPlayerId,
          proposed_player_id: slot.proposedPlayerId,
          current_live_player_id_snapshot: slot.currentPlayerId,
          is_pinned: slot.isPinned,
          is_excluded_from_apply: slot.isExcludedFromApply || workspace.excludedMatchIds.includes(slot.matchId),
          edit_source: slot.editSource,
          assignment_status: 'accepted',
          reason: slot.editSource === 'captain' ? 'Captain-edited proposal' : 'Engine-generated proposal',
        }));
      if (assignmentRows.length > 0) {
        const { error: assignmentError } = await supabase.from('season_plan_assignments').insert(assignmentRows);
        if (assignmentError) throw new Error(assignmentError.message);
      }
      setWorkspace({ ...workspace, status: 'applied', lastEditedAt: new Date().toISOString() });
      setMessage({ severity: 'success', text: 'Proposed season lineups applied. Published and protected status rules were preserved.' });
    } catch (error) {
      setMessage({ severity: 'error', text: error instanceof Error ? error.message : 'Unable to apply the proposed season plan.' });
    } finally {
      setLoading(false);
    }
  };

  const proposalMatches = useMemo(() => planningContext?.matches || [], [planningContext]);
  const projectionByMatchId = useMemo(() => new Map((selectedPlan?.matchProjections || []).map((projection) => [projection.matchId, projection])), [selectedPlan]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Season Planning</Typography>
          <Typography color="text.secondary">Generate baseline and rolling remaining-season lineup plans for captain review.</Typography>
        </Box>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />} onClick={() => void buildAnalysis()} disabled={loading || !teamId}>
          Generate Season Plan
        </Button>
      </Stack>
      {message && <Alert severity={message.severity} sx={{ mb: 2 }}>{message.text}</Alert>}
      {defaultCaptainTeamIds.length === 0 && !teamId && <Alert severity="info" sx={{ mb: 2 }}>You aren't assigned as captain or co-captain of a team. Select a team to plan for.</Alert>}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <FormControl sx={{ minWidth: 260 }} size="small">
            <InputLabel>Team</InputLabel>
            <Select label="Team" value={teamId} onChange={(event) => { setTeamId(event.target.value); setTeamScopeMode('explicit'); }}>
              {lookups.teams.map((team) => <MenuItem key={team.value} value={team.value}>{team.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Workflow</InputLabel>
            <Select label="Workflow" value={mode} onChange={(event) => setMode(event.target.value as 'season_baseline' | 'remaining_season')}>
              <MenuItem value="season_baseline">Generate Season Baseline</MenuItem>
              <MenuItem value="remaining_season">Rebalance Remaining Season</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel>Unknown Availability</InputLabel>
            <Select label="Unknown Availability" value={unknownPolicy} onChange={(event) => setUnknownPolicy(event.target.value as UnknownAvailabilityPolicy)}>
              <MenuItem value="exclude">Exclude Unknown</MenuItem>
              <MenuItem value="include_with_warning">Include With Warning</MenuItem>
              <MenuItem value="assume_available_for_planning">Assume Available</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox checked={includeTentative} onChange={(event) => setIncludeTentative(event.target.checked)} />} label="Include tentative" />
          <FormControlLabel control={<Checkbox checked={preserveDraft} onChange={(event) => setPreserveDraft(event.target.checked)} />} label="Preserve Draft" />
          <FormControlLabel control={<Checkbox checked={includePublished} onChange={(event) => setIncludePublished(event.target.checked)} />} label="Allow Published edits" />
        </Stack>
      </Paper>
      {analysis?.unresolvedConstraints.length ? (
        <Alert severity="warning" sx={{ mb: 2 }}>{analysis.unresolvedConstraints.join(' ')}</Alert>
      ) : null}
      {analysis && (
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
            {analysis.plans.map((plan) => (
              <Card key={plan.id} variant={selectedPlan?.id === plan.id ? 'elevation' : 'outlined'} sx={{ flex: 1, cursor: 'pointer', borderColor: selectedPlan?.id === plan.id ? 'primary.main' : undefined }} onClick={() => setSelectedPlanId(plan.id)}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{plan.name}</Typography>
                    {plan.recommended && <Chip color="primary" label="Recommended" size="small" />}
                  </Stack>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{plan.overallScore}</Typography>
                  <Chip color={confidenceColor(plan.confidence)} label={`${plan.confidence} confidence`} size="small" />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{plan.benefits[1]}</Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
          {selectedPlan && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedPlan.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {analysis.includedMatchIds.length} matches included. {analysis.excludedMatches.length} protected or excluded.
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<AutoFixHighIcon />} disabled={readOnly || loading || !planningContext} onClick={() => openWorkspace(selectedPlan)}>
                  Review Proposed Lineups
                </Button>
              </Stack>
              {selectedPlan.materialIssues.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{selectedPlan.materialIssues.slice(0, 3).join(' ')}</Alert>}
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Match Plan</Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Match</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell align="right">Changes</TableCell>
                      <TableCell>Drivers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPlan.matchProjections.map((match) => (
                      <TableRow key={match.matchId}>
                        <TableCell>{match.matchDate} vs {match.opponentName}</TableCell>
                        <TableCell>{match.lineupStatus}</TableCell>
                        <TableCell><Chip size="small" color={confidenceColor(match.confidence)} label={match.confidence} /></TableCell>
                        <TableCell align="right">{match.changedAssignments}</TableCell>
                        <TableCell>{match.confidenceDrivers.join(' ') || 'All assigned players are confirmed available with sufficient rating context.'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Player Projection</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Player</TableCell>
                      <TableCell align="right">Target</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Proposed</TableCell>
                      <TableCell align="right">Delta</TableCell>
                      <TableCell>Scarcity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPlan.playerProjections.map((projection) => (
                      <TableRow key={projection.playerId}>
                        <TableCell>{projection.playerName}</TableCell>
                        <TableCell align="right">{projection.targetMatches}</TableCell>
                        <TableCell align="right">{projection.completedPlayed}</TableCell>
                        <TableCell align="right">{projection.proposedFuture}</TableCell>
                        <TableCell align="right">{projection.projectedDelta > 0 ? `+${projection.projectedDelta}` : projection.projectedDelta}</TableCell>
                        <TableCell>{projection.scarcity.explanation}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
          {workspace && planningContext && (
            <Paper variant="outlined" sx={{ p: 2, borderColor: validation.blockingErrors.length > 0 ? 'error.main' : 'primary.main' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Review Proposed Season Lineups</Typography>
                    <Chip color="info" label="Proposed Plan - Not Yet Applied" size="small" />
                    <Chip color={validation.blockingErrors.length ? 'error' : 'success'} label={validation.blockingErrors.length ? `${validation.blockingErrors.length} blocking` : 'Valid to apply'} size="small" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {planningContext.teamLabel} • {planningContext.strategy.name} • {workspace.planName} • {proposalMatches.length} matches • {validation.eligibleMatches} eligible • {validation.protectedMatches} protected • {validation.changedAssignments} proposed changes
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <Button variant="outlined" startIcon={<SaveIcon />} onClick={() => void saveWorkspace()} disabled={readOnly || loading}>Save Proposed Edits</Button>
                  <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetWorkspace} disabled={loading}>Reset to Generated</Button>
                  <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={discardWorkspace} disabled={loading}>Discard</Button>
                  <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={() => setApplyDialogOpen(true)} disabled={readOnly || loading || validation.blockingErrors.length > 0 || validation.changedAssignments === 0}>
                    Review Changes
                  </Button>
                </Stack>
              </Stack>
              {validation.blockingErrors.length > 0 && <Alert severity="error" sx={{ mb: 1 }}>{validation.blockingErrors.slice(0, 4).join(' ')}</Alert>}
              {validation.warnings.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>{validation.warnings.slice(0, 4).join(' ')}</Alert>}
              <Stack spacing={2}>
                {proposalMatches.map((match) => {
                  const matchSlots = workspace.slots.filter((slot) => slot.matchId === match.id);
                  const protectedReason = protectedMatchReason(match, planningContext.includePublished);
                  const excluded = workspace.excludedMatchIds.includes(match.id);
                  const projection = projectionByMatchId.get(match.id);
                  return (
                    <Paper key={match.id} variant="outlined" sx={{ p: 2, bgcolor: protectedReason ? 'action.hover' : 'background.paper' }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>{match.matchDate} vs {match.opponentName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {match.matchSiteType} • {match.planningPriority || 'normal'} priority • Match {match.matchStatus} • Lineup {match.lineupStatus}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          {projection && <Chip size="small" color={confidenceColor(projection.confidence)} label={`${projection.confidence} confidence`} />}
                          {protectedReason && <Chip size="small" color="default" label={protectedReason} />}
                          {!protectedReason && (
                            <FormControlLabel
                              control={<Checkbox checked={excluded} onChange={() => toggleMatchExcluded(match.id)} />}
                              label="Exclude from apply"
                            />
                          )}
                        </Stack>
                      </Stack>
                      <Divider sx={{ mb: 1 }} />
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Line</TableCell>
                              <TableCell>Current Lineup</TableCell>
                              <TableCell>Proposed Lineup</TableCell>
                              <TableCell>Controls</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {matchSlots.map((slot) => {
                              const position = positionsById.get(slot.leagueConfigurationPositionId);
                              const currentPlayer = slot.currentPlayerId ? playersById.get(slot.currentPlayerId) : undefined;
                              const proposedPlayer = slot.proposedPlayerId ? playersById.get(slot.proposedPlayerId) : undefined;
                              const availability = slot.proposedPlayerId ? match.availabilityByPlayerId[slot.proposedPlayerId] || 'unknown' : undefined;
                              const disabled = Boolean(readOnly || loading || protectedReason || excluded || workspace.status === 'applied');
                              const assignedElsewhere = new Set(matchSlots.filter((item) => item.proposedPlayerId && slotKey(item) !== slotKey(slot)).map((item) => item.proposedPlayerId));
                              return (
                                <TableRow key={slotKey(slot)} selected={slot.currentPlayerId !== slot.proposedPlayerId}>
                                  <TableCell>{slot.positionCode} Slot {slot.slotNumber}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{playerLabel(currentPlayer, userPreferences)}</Typography>
                                    <Typography variant="caption" color="text.secondary">{playerDetail(currentPlayer, position?.lineType === 'singles' ? 'singles' : 'doubles', currentPlayer?.id ? match.availabilityByPlayerId[currentPlayer.id] : undefined)}</Typography>
                                  </TableCell>
                                  <TableCell sx={{ minWidth: 280 }}>
                                    <FormControl fullWidth size="small">
                                      <Select
                                        value={slot.proposedPlayerId || ''}
                                        disabled={disabled}
                                        displayEmpty
                                        onChange={(event) => updateSlot(slot, event.target.value ? String(event.target.value) : null)}
                                      >
                                        <MenuItem value="">Empty</MenuItem>
                                        {planningContext.players.map((player) => {
                                          const playerAvailability = match.availabilityByPlayerId[player.id] || 'unknown';
                                          const hardDisabled = playerAvailability === 'not_available' || assignedElsewhere.has(player.id);
                                          return (
                                            <MenuItem key={player.id} value={player.id} disabled={hardDisabled}>
                                              {playerLabel(player, userPreferences)} - {playerDetail(player, position?.lineType === 'singles' ? 'singles' : 'doubles', playerAvailability)}
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    </FormControl>
                                    <Typography variant="caption" color={slot.editSource === 'captain' ? 'primary.main' : 'text.secondary'}>
                                      Original Recommendation: {playerLabel(slot.generatedPlayerId ? playersById.get(slot.generatedPlayerId) : undefined, userPreferences)} • Edited by {slot.editedByName}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                      <Button size="small" disabled={disabled} onClick={() => updateSlot(slot, null)}>Clear</Button>
                                      <Button size="small" disabled={disabled} onClick={() => updateSlot(slot, slot.generatedPlayerId, 'restored')}>Restore</Button>
                                      <Button size="small" startIcon={<PushPinIcon />} disabled={Boolean(protectedReason || excluded || workspace.status === 'applied')} onClick={() => togglePin(slot)}>
                                        {slot.isPinned ? 'Unpin' : 'Pin'}
                                      </Button>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>
          )}
        </Stack>
      )}
      <Dialog open={applyDialogOpen} onClose={() => setApplyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Changes Before Applying</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>{proposalMatches.length} matches in proposal</Typography>
            <Typography>{validation.eligibleMatches} matches eligible for changes</Typography>
            <Typography>{validation.protectedMatches} protected matches</Typography>
            <Typography>{validation.added} assignments added</Typography>
            <Typography>{validation.replaced} assignments replaced</Typography>
            <Typography>{validation.removed} assignments removed</Typography>
            <Typography>{validation.publishedAffected} Published lineup changes</Typography>
            {validation.blockingErrors.length > 0 && <Alert severity="error">{validation.blockingErrors.join(' ')}</Alert>}
            {validation.warnings.length > 0 && <Alert severity="warning">{validation.warnings.slice(0, 5).join(' ')}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void applyWorkspace()} disabled={loading || validation.blockingErrors.length > 0 || validation.changedAssignments === 0}>
            Apply All Eligible Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
